import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from '../base/baseQuery'

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== null),
  )

export const sessionServiceApi = createApi({
  reducerPath: 'sessionServiceApi',
  baseQuery,
  tagTypes: ['Session'],
  endpoints: (builder) => ({
    getSessions: builder.query({
      query: (params) => ({ url: '/backoffice/sessions', params: cleanParams(params) }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Session', id })),
              { type: 'Session', id: 'LIST' },
            ]
          : [{ type: 'Session', id: 'LIST' }],
    }),
    getSessionById: builder.query({
      query: (id) => `/backoffice/sessions/${id}`,
      providesTags: (result, error, id) => [{ type: 'Session', id }],
    }),
    getUpcomingSessionsForGroup: builder.query({
      query: (groupId) => `/backoffice/groups/${groupId}/sessions/upcoming`,
      providesTags: (result, error, groupId) => [{ type: 'Session', id: `upcoming-${groupId}` }],
    }),
    addSession: builder.mutation({
      query: (body) => ({ url: '/backoffice/sessions', method: 'POST', body }),
      invalidatesTags: [{ type: 'Session', id: 'LIST' }],
    }),
    updateSession: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/backoffice/sessions/${id}`, method: 'PUT', body }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Session', id },
        { type: 'Session', id: 'LIST' },
      ],
    }),
    // Answer keys go to their own endpoint rather than through updateSession:
    // the backend freezes a COMPLETED session against edits, and COMPLETED is
    // exactly the state a corrigé is published in.
    addSessionCorrectionFiles: builder.mutation({
      query: ({ id, body }) => ({
        url: `/backoffice/sessions/${id}/correction-files`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Session', id },
        { type: 'Session', id: 'LIST' },
      ],
    }),
    removeSessionCorrectionFile: builder.mutation({
      query: ({ id, fileId }) => ({
        url: `/backoffice/sessions/${id}/correction-files/${fileId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Session', id },
        { type: 'Session', id: 'LIST' },
      ],
    }),
    // Runs the recording sweep now instead of waiting for the ten-minute cron.
    // Takes no argument on purpose: the backend scans every COMPLETED session
    // still missing a recording, so this is a global action rather than a
    // per-session one, and invalidating the whole list is what's correct —
    // any row in it could have just gained a recording.
    reconcileSessionRecordings: builder.mutation({
      query: () => ({
        url: '/backoffice/sessions/recordings/reconcile',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Session', id: 'LIST' }],
    }),
    changeSessionStatus: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/backoffice/sessions/${id}/status`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Session', id },
        { type: 'Session', id: 'LIST' },
      ],
    }),
    deleteSession: builder.mutation({
      query: (id) => ({ url: `/backoffice/sessions/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Session', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetSessionsQuery,
  useGetSessionByIdQuery,
  useGetUpcomingSessionsForGroupQuery,
  useAddSessionMutation,
  useUpdateSessionMutation,
  useChangeSessionStatusMutation,
  useAddSessionCorrectionFilesMutation,
  useRemoveSessionCorrectionFileMutation,
  useReconcileSessionRecordingsMutation,
  useDeleteSessionMutation,
} = sessionServiceApi
